import React, { useState, useEffect, useRef } from 'react';
import { PHASE } from '../hooks/useGameState';

const CELL_W = 32;
const CELL_H = 26;
const GRID_LEFT = 140;
const GRID_TOP = 55;

function cellPos(num, cols, cellW, cellH, gridLeft, gridTop) {
  const idx = num - 1;
  return {
    x: gridLeft + (idx % cols) * cellW + cellW / 2,
    y: gridTop + Math.floor(idx / cols) * cellH + cellH / 2,
  };
}

function LowPolyChicken({ x, y, flipped, sitting, walking }) {
  const f = flipped ? -1 : 1;
  return (
    <g transform={`translate(${x},${y}) scale(${f},1)`}>
      {/* Body - low poly faceted */}
      <polygon points="-14,0 -8,-14 6,-16 16,-8 14,4 4,12 -10,10" fill="#c8a882" />
      <polygon points="-14,0 -8,-14 -2,-10 -4,4" fill="#b8957a" />
      <polygon points="-2,-10 6,-16 16,-8 8,-4" fill="#d4b48e" />
      <polygon points="8,-4 16,-8 14,4 6,6" fill="#bea07c" />
      <polygon points="-4,4 -2,-10 6,6" fill="#c0976e" />
      {/* Tail feathers */}
      <polygon points="-14,0 -20,-6 -18,4" fill="#d8c4a8" />
      <polygon points="-14,0 -22,-2 -20,6" fill="#e0ceb0" />
      <polygon points="-14,0 -20,6 -14,10" fill="#c8b090" />
      {/* Wing */}
      <polygon points="-4,4 6,6 4,14 -8,12" fill="#bea07c" />
      <polygon points="6,6 14,4 10,12 4,14" fill="#d0ae8a" />
      {/* Head group with pecking animation */}
      <g style={{ transformOrigin: '20px -7px', animation: walking ? 'peck 0.4s ease infinite alternate' : 'none' }}>
        <polygon points="14,-8 20,-12 24,-6 20,0 14,-2" fill="#c8a882" />
        <polygon points="20,-12 24,-6 22,-14" fill="#b8957a" />
        {/* Comb */}
        <polygon points="18,-12 20,-18 22,-12" fill="#c0392b" />
        <polygon points="21,-11 23,-17 25,-11" fill="#c0392b" />
        <polygon points="16,-11 18,-16 20,-11" fill="#e74c3c" />
        {/* Wattle */}
        <ellipse cx="22" cy="-2" rx="3" ry="5" fill="#c0392b" />
        {/* Eye */}
        <circle cx="20" cy="-7" r="2.5" fill="white" />
        <circle cx="21" cy="-7" r="1.5" fill="#1a1a1a" />
        {/* Beak */}
        <polygon points="24,-6 30,-5 24,-3" fill="#e8890a" />
      </g>
      {/* Legs */}
      {!sitting && (
        <>
          <line x1="0" y1="12" x2="-4" y2="22" stroke="#e8890a" strokeWidth="2" />
          <line x1="6" y1="12" x2="8" y2="22" stroke="#e8890a" strokeWidth="2" />
          <line x1="-4" y1="22" x2="-8" y2="24" stroke="#e8890a" strokeWidth="1.5" />
          <line x1="-4" y1="22" x2="-2" y2="26" stroke="#e8890a" strokeWidth="1.5" />
          <line x1="-4" y1="22" x2="-6" y2="27" stroke="#e8890a" strokeWidth="1.5" />
          <line x1="8" y1="22" x2="4" y2="26" stroke="#e8890a" strokeWidth="1.5" />
          <line x1="8" y1="22" x2="10" y2="26" stroke="#e8890a" strokeWidth="1.5" />
        </>
      )}
      {sitting && (
        <>
          <polygon points="-8,12 8,12 10,18 -10,18" fill="#b8957a" />
          <line x1="-6" y1="18" x2="-10" y2="22" stroke="#e8890a" strokeWidth="2" />
          <line x1="6" y1="18" x2="10" y2="22" stroke="#e8890a" strokeWidth="2" />
        </>
      )}
    </g>
  );
}

function GoldenEgg({ x, y }) {
  return (
    <g style={{ animation: 'eggPulse 1s ease-in-out infinite alternate' }}>
      <ellipse cx={x} cy={y + 6} rx="9" ry="11" fill="#c8960a" />
      <ellipse cx={x} cy={y + 6} rx="7" ry="9" fill="#F0B800" />
      <ellipse cx={x - 2} cy={y + 2} rx="3" ry="2" fill="#ffe066" opacity="0.8" />
      <text x={x} y={y - 8} textAnchor="middle" fontSize="12" fill="#F0B800">✨</text>
    </g>
  );
}

function Coop({ chickenInside }) {
  return (
    <g>
      {/* Main building */}
      <rect x="8" y="95" width="108" height="72" fill="#c8956a" rx="2" />
      {/* Siding lines */}
      <line x1="8" y1="105" x2="116" y2="105" stroke="#b07850" strokeWidth="1" />
      <line x1="8" y1="118" x2="116" y2="118" stroke="#b07850" strokeWidth="1" />
      <line x1="8" y1="131" x2="116" y2="131" stroke="#b07850" strokeWidth="1" />
      <line x1="8" y1="144" x2="116" y2="144" stroke="#b07850" strokeWidth="1" />
      <line x1="8" y1="157" x2="116" y2="157" stroke="#b07850" strokeWidth="1" />
      {/* Main roof - angular asymmetric */}
      <polygon points="2,97 62,52 118,88 118,97 2,97" fill="#7a1a0a" />
      <polygon points="62,52 90,60 118,88 90,75" fill="#8B1a0a" />
      {/* Dormer */}
      <polygon points="72,72 90,60 108,72" fill="#6a1008" />
      <rect x="78" y="72" width="24" height="16" fill="#7a1a0a" />
      <rect x="82" y="74" width="8" height="10" fill="#87ceeb" stroke="#5a1008" strokeWidth="1" />
      {/* Chimney */}
      <rect x="30" y="48" width="12" height="24" fill="#5a3020" />
      <rect x="28" y="46" width="16" height="5" fill="#3a2010" />
      {/* Door frame */}
      <rect x="42" y="130" width="38" height="37" fill="#3d1a00" rx="2" />
      <rect x="44" y="132" width="34" height="35" fill="#2a1000" rx="1" />
      {/* Door open slightly */}
      <rect x="44" y="132" width="16" height="35" fill="#4a2800" rx="1" />
      {/* Window */}
      <rect x="14" y="108" width="22" height="18" fill="#87ceeb" stroke="#5a3020" strokeWidth="2" rx="1" />
      <line x1="25" y1="108" x2="25" y2="126" stroke="#5a3020" strokeWidth="1" />
      <line x1="14" y1="117" x2="36" y2="117" stroke="#5a3020" strokeWidth="1" />
      {/* Sign */}
      <rect x="18" y="90" width="88" height="14" fill="#F0B800" rx="3" />
      <text x="62" y="101" textAnchor="middle" fontSize="8" fontWeight="bold" fill="#5a3e00">CHICKEN COOP</text>
      {/* Chicken inside peeking */}
      {chickenInside && (
        <g transform="translate(62,150) scale(0.6,0.6)">
          <circle cx="0" cy="-8" r="8" fill="#c8a882" />
          <polygon points="-4,-14 0,-20 4,-14" fill="#c0392b" />
          <circle cx="3" cy="-9" r="2" fill="#1a1a1a" />
        </g>
      )}
    </g>
  );
}

function CartoonFence({ W, H }) {
  const pickets = [];
  const heights = [22, 18, 24, 20, 26, 19, 23, 21, 25, 18, 22, 24, 20, 23, 19, 21, 26, 18, 24, 22];
  let hi = 0;

  // Top fence
  for (let x = 10; x < W - 10; x += 13) {
    const h = heights[hi % heights.length];
    hi++;
    pickets.push(
      <g key={`t${x}`}>
        <rect x={x} y={8} width={7} height={h} fill="white" rx="1" />
        <polygon points={`${x},8 ${x + 3.5},2 ${x + 7},8`} fill="white" />
      </g>
    );
  }
  // Bottom fence
  hi = 5;
  for (let x = 10; x < W - 10; x += 13) {
    const h = heights[hi % heights.length];
    hi++;
    pickets.push(
      <g key={`b${x}`}>
        <rect x={x} y={H - h - 8} width={7} height={h} fill="white" rx="1" />
        <polygon points={`${x},${H - h - 8} ${x + 3.5},${H - h - 14} ${x + 7},${H - h - 8}`} fill="white" />
      </g>
    );
  }
  // Left fence
  hi = 10;
  for (let y = 36; y < H - 36; y += 13) {
    const h = heights[hi % heights.length];
    hi++;
    pickets.push(
      <g key={`l${y}`}>
        <rect x={8} y={y} width={h} height={7} fill="white" rx="1" />
        <polygon points={`8,${y} 2,${y + 3.5} 8,${y + 7}`} fill="white" />
      </g>
    );
  }
  // Right fence
  hi = 15;
  for (let y = 36; y < H - 36; y += 13) {
    const h = heights[hi % heights.length];
    hi++;
    pickets.push(
      <g key={`r${y}`}>
        <rect x={W - h - 8} y={y} width={h} height={7} fill="white" rx="1" />
        <polygon points={`${W - 8},${y} ${W - 2},${y + 3.5} ${W - 8},${y + 7}`} fill="white" />
      </g>
    );
  }

  return (
    <g>
      {pickets}
      <rect x="8" y="16" width={W - 16} height="4" fill="white" opacity="0.9" />
      <rect x="8" y="22" width={W - 16} height="3" fill="white" opacity="0.7" />
      <rect x="8" y={H - 22} width={W - 16} height="4" fill="white" opacity="0.9" />
      <rect x="8" y={H - 16} width={W - 16} height="3" fill="white" opacity="0.7" />
      <rect x="16" y="34" width="4" height={H - 68} fill="white" opacity="0.9" />
      <rect x="22" y="34" width="3" height={H - 68} fill="white" opacity="0.7" />
      <rect x={W - 26} y="34" width="4" height={H - 68} fill="white" opacity="0.9" />
      <rect x={W - 20} y="34" width="3" height={H - 68} fill="white" opacity="0.7" />
    </g>
  );
}

export default function ChickenArena({ phase, walkPath, chickenPos, winningSquare, squares, resultMessage, resultCountdown, publicKey, onSquareClick, mySquares = [], gridSize = 10, poolTotal = 0, settledPrize = null }) {
  const totalSquares = gridSize * gridSize;
  const W = GRID_LEFT + gridSize * CELL_W + 60;
  const H = GRID_TOP + gridSize * CELL_H + 55;
  const getPos = (num) => cellPos(num, gridSize, CELL_W, CELL_H, GRID_LEFT, GRID_TOP);

  const [chickenXY, setChickenXY] = useState({ x: 75, y: 148 });
  const [flipped, setFlipped] = useState(false);
  const [sitting, setSitting] = useState(false);
  const [showEgg, setShowEgg] = useState(false);
  const [eggPos, setEggPos] = useState(null);
  const [showCloseup, setShowCloseup] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [animFrame, setAnimFrame] = useState(0);
  const [hoveredSquare, setHoveredSquare] = useState(null);
  const prevPos = useRef({ x: 75, y: 148 });

  useEffect(() => {
    const t = setInterval(() => setAnimFrame(f => f + 1), 350);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (phase === PHASE.WALKING || phase === PHASE.COUNTDOWN) {
      setIsFullscreen(true);
    }
    if (phase === PHASE.WAITING) {
      setIsFullscreen(false);
      setSitting(false);
      setShowEgg(false);
      setEggPos(null);
      setShowCloseup(false);
      setChickenXY({ x: 75, y: 148 });
    }
  }, [phase]);

  useEffect(() => {
    if (phase === PHASE.WALKING && chickenPos > 0 && walkPath[chickenPos - 1] != null) {
      const sq = walkPath[chickenPos - 1];
      const pos = getPos(sq);
      const prev = prevPos.current;
      setFlipped(pos.x < prev.x);
      prevPos.current = pos;
      setChickenXY(pos);
      setSitting(false);
      setShowEgg(false);
    } else if (phase === PHASE.RESULT && winningSquare) {
      const adjSquare = winningSquare < totalSquares ? winningSquare + 1 : winningSquare - 1;
      const adjPos = getPos(adjSquare);
      setChickenXY(adjPos);
      setSitting(false);
      setShowEgg(false);
      setTimeout(() => {
        const winPos = getPos(winningSquare);
        setChickenXY(winPos);
        setSitting(true);
        setTimeout(() => {
          setShowCloseup(true);
          playChickenSound();
          setTimeout(() => {
            setShowCloseup(false);
            setEggPos({ x: winPos.x + 18, y: winPos.y + 8 });
            setShowEgg(true);
          }, 12000);
        }, 600);
      }, 800);
    }
  }, [phase, chickenPos, walkPath, winningSquare]);

  const playChickenSound = () => {
    try {
      const audio = new Audio('/chicken.mp3');
      audio.volume = 0.8;
      audio.play().catch(() => {});
    } catch(e) {}
  };

  const isWon = resultMessage && resultMessage.includes('Congratulations');
  const isLost = resultMessage && resultMessage.includes('Better luck');

  const containerStyle = isFullscreen ? {
    position: 'fixed',
    inset: 0,
    zIndex: 200,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(0,0,0,0.92)',
    transition: 'all 0.4s ease',
  } : {
    width: '100%',
    transition: 'all 0.4s ease',
  };

  const svgStyle = isFullscreen ? {
    width: '90vw',
    maxWidth: '900px',
    height: 'auto',
    filter: 'drop-shadow(0 0 40px rgba(240,184,0,0.3))',
  } : {
    width: '100%',
    height: 'auto',
  };

  return (
    <div style={containerStyle}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        style={svgStyle}
      >
        <style>{`
          @keyframes peck {
            from { transform: rotate(-15deg); }
            to { transform: rotate(10deg); }
          }
          @keyframes eggPulse {
            from { filter: drop-shadow(0 0 4px #F0B800); opacity: 1; }
            to { filter: drop-shadow(0 0 14px #F0B800); opacity: 0.85; }
          }
          @keyframes zoomIn {
            from { transform: scale(0.2); opacity: 0; }
            to { transform: scale(1); opacity: 1; }
          }
          @keyframes buhgaawk {
            0% { transform: scale(0.1) rotate(-10deg); opacity: 0; }
            50% { transform: scale(1.2) rotate(5deg); opacity: 1; }
            100% { transform: scale(1) rotate(0deg); opacity: 1; }
          }
          @keyframes squarePulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.55; }
          }
          @keyframes beakOpenDelayed {
            0%, 8% { transform: rotate(0deg) translateY(0px); }
            12% { transform: rotate(22deg) translateY(5px); }
            18% { transform: rotate(0deg) translateY(0px); }
            22% { transform: rotate(18deg) translateY(4px); }
            28% { transform: rotate(0deg) translateY(0px); }
            33% { transform: rotate(22deg) translateY(5px); }
            40% { transform: rotate(5deg) translateY(1px); }
            45% { transform: rotate(20deg) translateY(4px); }
            52% { transform: rotate(0deg) translateY(0px); }
            57% { transform: rotate(22deg) translateY(5px); }
            63% { transform: rotate(0deg) translateY(0px); }
            68% { transform: rotate(18deg) translateY(4px); }
            74% { transform: rotate(0deg) translateY(0px); }
            78% { transform: rotate(22deg) translateY(5px); }
            84% { transform: rotate(8deg) translateY(2px); }
            88% { transform: rotate(20deg) translateY(5px); }
            94% { transform: rotate(0deg) translateY(0px); }
            97% { transform: rotate(10deg) translateY(2px); }
            100% { transform: rotate(0deg) translateY(0px); }
          }
        `}</style>
        <defs>
          <pattern id="grass" patternUnits="userSpaceOnUse" width="16" height="16">
            <rect width="16" height="16" fill="#3a7a22" />
            <rect x="0" y="0" width="8" height="8" fill="#357020" opacity="0.4" />
            <rect x="8" y="8" width="8" height="8" fill="#357020" opacity="0.4" />
          </pattern>
        </defs>

        {/* Background grass */}
        <rect width={W} height={H} fill="url(#grass)" />

        {/* Fence */}
        <CartoonFence W={W} H={H} />

        {/* Coop */}
        <Coop chickenInside={phase === PHASE.WAITING} />

        {/* Grid dirt base */}
        <rect x={GRID_LEFT - 5} y={GRID_TOP - 5} width={gridSize * CELL_W + 10} height={gridSize * CELL_H + 10} fill="#c8a96e" rx="4" stroke="#8B6914" strokeWidth="1.5" />

        {/* Grid cells */}
        {Array.from({ length: totalSquares }, (_, i) => {
          const num = i + 1;
          const col = i % gridSize;
          const row = Math.floor(i / gridSize);
          const x = GRID_LEFT + col * CELL_W;
          const y = GRID_TOP + row * CELL_H;
          const owned = squares && squares[num];
          const isWinner = num === winningSquare;
          const isVisiting = phase === PHASE.WALKING && walkPath && chickenPos > 0 && walkPath[chickenPos - 1] === num;
          const isMySquare = mySquares.includes(num);
          const isOwned = !!owned && !isMySquare;

          return (
            <g
              key={num}
              onClick={() => onSquareClick && phase === 'waiting' && !owned && onSquareClick(num)}
              onMouseEnter={() => setHoveredSquare(num)}
              onMouseLeave={() => setHoveredSquare(null)}
              style={{ cursor: phase === 'waiting' && !owned ? 'pointer' : 'default' }}
            >
              <rect
                x={x + 0.5} y={y + 0.5}
                width={CELL_W - 1} height={CELL_H - 1}
                fill={
                  isWinner ? '#F0B800' :
                  isVisiting ? '#F0B800' :
                  isMySquare ? '#4CAF50' :
                  isOwned ? '#4A90E2' :
                  '#F0B800'
                }
                fillOpacity={
                  isWinner ? 1 :
                  isVisiting ? 0.4 :
                  isMySquare ? 0.9 :
                  isOwned ? 0.7 :
                  hoveredSquare === num && phase === 'waiting' ? 0.4 :
                  0.15
                }
                stroke={
                  isWinner ? '#c8960a' :
                  isVisiting ? '#F0B800' :
                  isMySquare ? '#F0B800' :
                  isOwned ? '#4A90E2' :
                  '#F0B800'
                }
                strokeWidth={isMySquare ? 3 : isOwned ? 1.5 : 0.5}
                rx="1"
                style={isMySquare ? { animation: 'squarePulse 2s ease-in-out infinite' } : undefined}
              />
              <text
                x={x + CELL_W / 2}
                y={y + CELL_H / 2 + 3}
                textAnchor="middle"
                fontSize="6.5"
                fontWeight="bold"
                fill={isWinner ? '#000' : (isMySquare || isOwned) ? '#fff' : '#5a3e10'}
              >
                {isWinner ? '🥚' : num}
              </text>
              {isMySquare && !isWinner && (
                <text
                  x={x + CELL_W / 2}
                  y={y + 6}
                  textAnchor="middle"
                  fontSize="5"
                  fill="#F0B800"
                >
                  ★
                </text>
              )}
            </g>
          );
        })}

        {/* Chicken - smooth position transition */}
        <g style={{ transform: `translate(${chickenXY.x}px, ${chickenXY.y}px)`, transition: 'transform 1.8s ease' }}>
          <g transform={`translate(0, ${animFrame % 2 === 0 && !sitting ? -1 : 0})`}>
            <LowPolyChicken
              x={0}
              y={0}
              flipped={flipped}
              sitting={sitting}
              walking={phase === PHASE.WALKING}
            />
          </g>
        </g>

        {/* Golden egg */}
        {showEgg && eggPos && (
          <GoldenEgg x={eggPos.x} y={eggPos.y} />
        )}

        {/* Arena label */}
        <rect x={W / 2 - 80} y="3" width="160" height="16" fill="#F0B800" rx="8" />
        <text x={W / 2} y="15" textAnchor="middle" fontSize="9" fontWeight="bold" fill="#5a3e00">CHICKEN DROP ARENA</text>

        {/* Result overlay inside SVG */}
        {phase === PHASE.RESULT && resultMessage && (
          <g>
            <rect x={W * 0.15} y={H * 0.32} width={W * 0.7} height={Math.max(130, H * 0.36)} fill="rgba(0,0,0,0.85)" rx="12" />
            <rect x={W * 0.15 + 2} y={H * 0.32 + 2} width={W * 0.7 - 4} height={Math.max(130, H * 0.36) - 4} fill={isWon ? '#1a3a1a' : isLost ? '#3a1a1a' : '#1a1a3a'} rx="11" stroke={isWon ? '#4CAF50' : isLost ? '#E05A5A' : '#F0B800'} strokeWidth="2" />
            <text x={W / 2} y={H * 0.32 + 52} textAnchor="middle" fontSize="18" fontWeight="bold" fill={isWon ? '#4CAF50' : isLost ? '#E05A5A' : '#F0B800'}>
              {isWon ? '🐔' : isLost ? '😔' : '🥚'}
            </text>
            <text x={W / 2} y={H * 0.32 + 77} textAnchor="middle" fontSize="10" fontWeight="bold" fill="white">
              {resultMessage.slice(2)}
            </text>
            <text x={W / 2} y={H * 0.32 + 104} textAnchor="middle" fontSize="10" fill="#aaa">
              New round in {resultCountdown}s...
            </text>
          </g>
        )}

        {/* Countdown phase overlay */}
        {phase === PHASE.COUNTDOWN && (
          <g>
            <rect x={W / 2 - 60} y={H * 0.38} width="120" height="70" fill="rgba(0,0,0,0.8)" rx="8" />
            <text x={W / 2} y={H * 0.38 + 35} textAnchor="middle" fontSize="14" fontWeight="bold" fill="#F0B800">GET READY!</text>
            <text x={W / 2} y={H * 0.38 + 60} textAnchor="middle" fontSize="11" fill="#aaa">Chicken entering arena...</text>
          </g>
        )}
      </svg>

      {showCloseup && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 300,
          background: 'rgba(0,0,0,0.95)',
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
        }}>
          <svg viewBox="-60 -80 120 120" style={{ width: '400px', height: '400px', animation: 'zoomIn 0.5s ease forwards' }}>
            <polygon points="-30,0 -15,-30 15,-35 35,-20 30,10 10,25 -20,22" fill="#c8a882" />
            <polygon points="-30,0 -15,-30 -5,-22 -8,8" fill="#b8957a" />
            <polygon points="-5,-22 15,-35 35,-20 18,-10" fill="#d4b48e" />
            <polygon points="18,-10 35,-20 30,10 12,12" fill="#bea07c" />
            <polygon points="-35,0 -42,-12 -38,8" fill="#d8c4a8" />
            <polygon points="-35,0 -45,-4 -40,12" fill="#e0ceb0" />
            <polygon points="-30,0 -20,-50 -10,-45 -5,-22" fill="#c8a882" />
            <polygon points="-20,-50 -10,-45 -5,-55" fill="#b8957a" />
            <polygon points="18,-12 28,-18 32,-10 24,-2 18,-4" fill="#c8a882" />
            <polygon points="28,-18 32,-10 30,-20" fill="#b8957a" />
            <polygon points="24,-14 26,-22 28,-14" fill="#c0392b" />
            <polygon points="27,-13 29,-20 31,-13" fill="#c0392b" />
            <polygon points="22,-13 24,-20 26,-13" fill="#e74c3c" />
            <ellipse cx="30" cy="-4" rx="4" ry="7" fill="#c0392b" />
            <circle cx="27" cy="-9" r="5" fill="white" />
            <circle cx="29" cy="-9" r="3" fill="#1a1a1a" />
            <circle cx="30" cy="-10" r="1" fill="white" />
            <g style={{ transformOrigin: '38px -7px', animation: 'beakOpenDelayed 12s ease forwards' }}>
              <polygon points="34,-8 46,-6 34,-4" fill="#e8890a" />
              <polygon points="34,-8 46,-6 34,-6" fill="#c8690a" />
            </g>
          </svg>
          <div style={{
            fontFamily: "'Bebas Neue', sans-serif",
            fontSize: '80px',
            color: '#F0B800',
            letterSpacing: '8px',
            opacity: 0,
            animation: 'buhgaawkText 0.5s ease 1s forwards',
            textShadow: '0 0 30px rgba(240,184,0,0.8)',
            marginTop: '-20px',
          }}>
            BUHGAAWK!
          </div>
          <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '28px', color: '#F0B800', letterSpacing: '3px', marginTop: 8, textAlign: 'center' }}>
            YOU'RE A WINNER CHICKEN DINNER!!! SQUARE {winningSquare}
          </div>
          <div style={{ fontFamily: "'DM Mono', monospace", fontSize: '16px', color: '#4CAF50', letterSpacing: '2px', marginTop: 8, textAlign: 'center' }}>
            WALLET: {squares && winningSquare && squares[winningSquare]?.walletAddress ? `${squares[winningSquare].walletAddress.slice(0,4)}...${squares[winningSquare].walletAddress.slice(-4)}` : '????'} WON {settledPrize || (poolTotal ? poolTotal.toFixed(3) : '0.000')} SOL!!!
          </div>
          <style>{`
            @keyframes zoomIn { from { transform: scale(0.2); opacity: 0; } to { transform: scale(1); opacity: 1; } }
            @keyframes buhgaawk { 0% { transform: scale(0.1) rotate(-10deg); opacity: 0; } 50% { transform: scale(1.2) rotate(5deg); opacity: 1; } 100% { transform: scale(1) rotate(0deg); opacity: 1; } }
            @keyframes buhgaawkText { 0% { transform: scale(0.1) rotate(-10deg); opacity: 0; } 50% { transform: scale(1.2) rotate(5deg); opacity: 1; } 100% { transform: scale(1) rotate(0deg); opacity: 1; } }
            @keyframes beakOpenDelayed {
              0%, 8% { transform: rotate(0deg) translateY(0px); }
              12% { transform: rotate(22deg) translateY(5px); }
              18% { transform: rotate(0deg) translateY(0px); }
              22% { transform: rotate(18deg) translateY(4px); }
              28% { transform: rotate(0deg) translateY(0px); }
              33% { transform: rotate(22deg) translateY(5px); }
              40% { transform: rotate(5deg) translateY(1px); }
              45% { transform: rotate(20deg) translateY(4px); }
              52% { transform: rotate(0deg) translateY(0px); }
              57% { transform: rotate(22deg) translateY(5px); }
              63% { transform: rotate(0deg) translateY(0px); }
              68% { transform: rotate(18deg) translateY(4px); }
              74% { transform: rotate(0deg) translateY(0px); }
              78% { transform: rotate(22deg) translateY(5px); }
              84% { transform: rotate(8deg) translateY(2px); }
              88% { transform: rotate(20deg) translateY(5px); }
              94% { transform: rotate(0deg) translateY(0px); }
              97% { transform: rotate(10deg) translateY(2px); }
              100% { transform: rotate(0deg) translateY(0px); }
            }
          `}</style>
        </div>
      )}
    </div>
  );
}
